import "./shared-navigation.scss";
import { ReactComponent as NavMobileHome } from "assets/images/v2/nav-mobile-home.svg";
import { ReactComponent as NavMobileTasks } from "assets/images/v2/nav-mobile-tasks.svg";
import { ReactComponent as NavMobileDocs } from "assets/images/v2/nav-mobile-docs.svg";
import { ReactComponent as NavMobileMore } from "assets/images/v2/nav-mobile-more.svg";
import { ReactComponent as NavDesktopHome } from "assets/images/v2/nav-desktop-home.svg";
import { ReactComponent as NavDesktopTasks } from "assets/images/v2/nav-desktop-tasks.svg";
import { ReactComponent as NavDesktopDocs } from "assets/images/v2/nav-desktop-docs.svg";
import { ReactComponent as NavDesktopLoanDetails } from "assets/images/v2/nav-desktop-loan-details.svg";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { getProfileSettings, getTaskCount } from "features/authSlice";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import TabletNavOverlay from "components/v2/tablet-nav-overlay/TabletNavOverlay";
import MobileBottomNav from "components/v2/mobile-bottom-nav/MobileBottomNav";
import { trackFSNavShellBottomNavTabSelect, type BottomNavTabId } from "utilities/full-story";

export interface NavigationPaneProps {
  activeTab?: string;
  readonly isDesktop: boolean;
  readonly isTablet: boolean;
  readonly onTabChange: (tab: string) => void;
  readonly isNavOpen: boolean;
  readonly setIsNavOpen: (open: boolean) => void;
  /**
   * ADO-521159 loan-centric nav flag state, forwarded so the mobile bottom
   * nav's ADO-541066 safe-area-inset fix stays scoped behind the flag per
   * that story's implementation-stays-under-the-flag directive.
   */
  readonly loanCentricSidebarNavEnabled?: boolean;
}

export enum NavigationIds {
  Home = "home",
  Tasks = "tasks",
  Docs = "documents",
  LoanDetails = "loan-details",
  More = "more",
}

const SharedNavigation: React.FC<NavigationPaneProps> = (props: NavigationPaneProps) => {
  const {
    activeTab = NavigationIds.Home,
    isDesktop,
    onTabChange,
    isTablet,
    isNavOpen,
    setIsNavOpen,
    loanCentricSidebarNavEnabled = false,
  } = props;
  const isProfile = useSelector(getProfileSettings);
  const taskCount = useSelector(getTaskCount);
  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const desktopTabs = [
    { id: NavigationIds.Home, label: "Home", icon: <NavDesktopHome />, order: 0 },
    { id: NavigationIds.Tasks, label: "Tasks", icon: <NavDesktopTasks />, badge: taskCount > 0 ? taskCount : null, order: 2 },
    { id: NavigationIds.Docs, label: "Documents", icon: <NavDesktopDocs />, order: 4 },
    ...(featureFlag?.loanDetailsEnabled
      ? [{ id: NavigationIds.LoanDetails, label: "Loan Details", icon: <NavDesktopLoanDetails />, order: 6 }]
      : []),
  ];

  const mobileTabs = [
    { id: NavigationIds.Home, label: "Home", icon: <NavMobileHome />, order: 0 },
    { id: NavigationIds.Tasks, label: "Tasks", icon: <NavMobileTasks />, badge: taskCount > 0 ? taskCount : null, order: 2 },
    { id: NavigationIds.Docs, label: "Documents", icon: <NavMobileDocs />, order: 4 },
    { id: NavigationIds.More, label: "More", icon: <NavMobileMore />, order: 6 },
  ];

  const handleBottomNavTabChange = (tab: string) => {
    trackFSNavShellBottomNavTabSelect({ tabId: tab as BottomNavTabId });
    onTabChange(tab);
  };

  const renderOverlayNavList = () => (
    <nav>
      <ul className="nav-list">
        {mobileTabs.map(({ id, label, icon, badge }) => (
          <li
            key={id}
            className={classNames("nav-item", "label-large-medium", { active: activeTab === id })}
            onClick={() => {
              onTabChange(id);
              setIsNavOpen(false);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onTabChange(id);
                setIsNavOpen(false);
              }
            }}
            role="tab"
            aria-selected={activeTab === id}
            tabIndex={0}
          >
            <span className="nav-icon">{icon}</span>
            <span className="nav-label">{label}</span>
            {badge && <span className="nav-badge label-medium-medium">{badge}</span>}
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <>
      {isDesktop && (
        <nav>
          <ul className="nav-list">
            {desktopTabs.map(({ id, label, icon, badge }) => (
              <li
                key={id}
                className={classNames("nav-item", "label-large-medium", { active: activeTab === id })}
                onClick={() => onTabChange(id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onTabChange(id);
                  }
                }}
                role="tab"
                aria-selected={activeTab === id}
                tabIndex={0}
              >
                <span className="nav-icon">{icon}</span>
                <span className="nav-label">{label}</span>
                {badge && <span className="nav-badge label-medium-medium">{badge}</span>}
              </li>
            ))}
          </ul>
        </nav>
      )}

      {!isDesktop && isTablet && !isProfile && (
        <TabletNavOverlay isOpen={isNavOpen} onClose={() => setIsNavOpen(false)}>
          {renderOverlayNavList()}
        </TabletNavOverlay>
      )}

      {!isDesktop && !isTablet && !isProfile && (
        <MobileBottomNav
          items={mobileTabs}
          activeTab={activeTab}
          onTabChange={handleBottomNavTabChange}
          safeAreaInsetEnabled={loanCentricSidebarNavEnabled}
        />
      )}
    </>
  );
};

export default SharedNavigation;
